import type { MappingProfileProjectMatch } from './mappingProjectReadiness';
import type { MappingProfileProjectCatalogSource } from './projectProfileGateTypes';

export type ProjectProfileGateDecisionMode = 'checking' | 'autoPass' | 'needsChoice';

export type ProjectProfileGateDecisionReason =
  | 'rememberedExactMatch'
  | 'rememberedProjectBinding'
  | 'trustedLiveFlow'
  | 'singleExactMatch'
  | 'multipleExactMatches'
  | 'noExactMatch'
  | 'catalogUnavailable'
  | 'loading';

export interface ProjectProfileGateDecision {
  mode: ProjectProfileGateDecisionMode;
  profileKey: string | null;
  reason: ProjectProfileGateDecisionReason;
}

export interface ResolveProjectProfileGateDecisionInput {
  enabled: boolean;
  isLoading: boolean;
  catalogReady: boolean;
  catalogSource: MappingProfileProjectCatalogSource;
  catalogError: string | null;
  matches: MappingProfileProjectMatch[];
  lastLoadedProfileKey: string | null;
  trustedProfileKey?: string | null;
  trustedProfileSource?: 'profile-load' | 'live-flow' | null;
}

export interface ResolveRememberedProjectBindingDecisionInput {
  enabled: boolean;
  isLoading: boolean;
  availableProfileKeys: readonly string[];
  preferredProfileKey: string | null;
  preferredProfileSource?: 'profile-load' | 'live-flow' | null;
}

export function canAutoLoadProjectProfileDecision(
  decision: ProjectProfileGateDecision
): boolean {
  return decision.mode === 'autoPass' &&
    (decision.reason === 'trustedLiveFlow' ||
      decision.reason === 'rememberedExactMatch' ||
      decision.reason === 'rememberedProjectBinding');
}

export function resolveRememberedProjectBindingDecision({
  enabled,
  isLoading,
  availableProfileKeys,
  preferredProfileKey,
  preferredProfileSource,
}: ResolveRememberedProjectBindingDecisionInput): ProjectProfileGateDecision | null {
  if (!enabled || !preferredProfileKey) {
    return null;
  }

  if (isLoading) {
    return { mode: 'checking', profileKey: null, reason: 'loading' };
  }

  // Project bindings outlive server profiles. Never auto-load a key that is no
  // longer visible to the current user, because the ensuing 400 leaves the
  // entire workspace gated with no usable selection.
  if (!availableProfileKeys.includes(preferredProfileKey)) {
    return { mode: 'needsChoice', profileKey: null, reason: 'noExactMatch' };
  }

  return {
    mode: 'autoPass',
    profileKey: preferredProfileKey,
    reason:
      preferredProfileSource === 'live-flow'
        ? 'trustedLiveFlow'
        : 'rememberedProjectBinding',
  };
}

function hasAuthoritativeProjectCatalog(
  catalogReady: boolean,
  catalogSource: MappingProfileProjectCatalogSource,
  catalogError: string | null
): boolean {
  return catalogReady && !catalogError && (catalogSource === 'project' || catalogSource === 'project+sampled');
}

function isSafeExactProjectMatch(match: MappingProfileProjectMatch): boolean {
  const projectAvailableCount = match.projectAvailableCount ?? match.availableCount;

  return match.status === 'match' && match.referenceCount > 0 && projectAvailableCount === match.referenceCount;
}

function isSafeAvailableProjectMatch(match: MappingProfileProjectMatch | null): match is MappingProfileProjectMatch {
  if (!match) {
    return false;
  }

  const projectAvailableCount = match.projectAvailableCount ?? match.availableCount;
  return match.referenceCount > 0 && projectAvailableCount > 0 && projectAvailableCount <= match.referenceCount;
}

export function resolveProjectProfileGateDecision({
  enabled,
  isLoading,
  catalogReady,
  catalogSource,
  catalogError,
  matches,
  lastLoadedProfileKey,
  trustedProfileKey,
  trustedProfileSource,
}: ResolveProjectProfileGateDecisionInput): ProjectProfileGateDecision {
  if (!enabled || isLoading) {
    return { mode: 'checking', profileKey: null, reason: 'loading' };
  }

  if (!hasAuthoritativeProjectCatalog(catalogReady, catalogSource, catalogError)) {
    return { mode: 'needsChoice', profileKey: null, reason: 'catalogUnavailable' };
  }

  const trustedLiveFlowMatch =
    trustedProfileSource === 'live-flow' && trustedProfileKey
      ? matches.find((match) => match.profileKey === trustedProfileKey) ?? null
      : null;
  if (trustedLiveFlowMatch && isSafeAvailableProjectMatch(trustedLiveFlowMatch)) {
    return {
      mode: 'autoPass',
      profileKey: trustedLiveFlowMatch.profileKey,
      reason: 'trustedLiveFlow',
    };
  }

  const exactMatches = matches.filter(isSafeExactProjectMatch);
  const rememberedExactMatch = lastLoadedProfileKey
    ? exactMatches.find((match) => match.profileKey === lastLoadedProfileKey) ?? null
    : null;

  if (rememberedExactMatch) {
    return {
      mode: 'autoPass',
      profileKey: rememberedExactMatch.profileKey,
      reason: 'rememberedExactMatch',
    };
  }

  if (exactMatches.length === 1) {
    return {
      mode: 'autoPass',
      profileKey: exactMatches[0].profileKey,
      reason: 'singleExactMatch',
    };
  }

  if (exactMatches.length > 1) {
    return { mode: 'needsChoice', profileKey: null, reason: 'multipleExactMatches' };
  }

  return { mode: 'needsChoice', profileKey: null, reason: 'noExactMatch' };
}
